import React from "react";
import { context } from "../../../../../utils/Provider";
import { styled } from "@mui/material/styles";
import { Table, TableContainer } from "@mui/material";
import { useHeadroomContext } from "../../context/HeadRoomProvider";
import { Column } from "../../HeadRoom";
import MDTypography from "components/MDTypography";
import TablePaginationWrapper, { MAX_ROWS } from "./TablePaginationWrapper";
import colors from "assets/theme-dark/base/colors";
import { useTableSort } from "../../hooks/useTableSort";
import { useSegmentedDriverMetrics } from "../../hooks/useSegmentedDriverMetrics";
import { useDriverTableSegments } from "../../hooks/useDriverTableSegment";
import DriverMetricsTableHeader from "./DriverMetricsTableHeader";
import DriverMetricsTableBody, { EditedMetrics } from "./DriverMetricsTableBody";
import FunctionsIcon from "@mui/icons-material/Functions";
import DownloadIcon from "@mui/icons-material/Download";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { validateDriversAndSegments } from "../../utils/validationUtils";
import { useTableHeaders } from "../../hooks/useSegmentationTableHeaders";
import TableToolbar from "./TableToolbar";
import { TooltipProps } from "@mui/material";
import { useHeadroomSaveData } from "../../hooks/useHeadroomSaveData";
import { exportDriverMetricsDataToExcel } from "../../utils/exportDriverMetricsToExcel";

interface DriverMetricsTableProps {
  columns: Column[];
}

const DriverMetricsTable: React.FC<DriverMetricsTableProps> = ({ columns }) => {
  const { setIsLoading } = React.useContext(context);
  const {
    segmentGroups,
    driver1Type,
    driver2Type,
    driver1Mode,
    driver2Mode,
    driver1NumeratorType,
    driver2NumeratorType,
    tableName,
    shouldRefetchDriverMetrics,
    setShouldRefetchDriverMetrics,
    driver1Percentile,
    driver2Percentile,
    headroomDriver,
    setDriver1Percentile,
    setDriver2Percentile,
    headroomDriverPercentile,
  } = useHeadroomContext();

  const {
    data,
    loading,
    error,
    totalCount,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    fetchData,
    hasNextPage,
    hasPreviousPage,
  } = useSegmentedDriverMetrics();

  const { selectableHeader1, selectableHeader2, headerOptions, setSelectableHeader1, setSelectableHeader2 } =
    useTableHeaders({ columns });

  const { saveDriverMetricsData, driverMetricsResult } = useHeadroomSaveData();
  const { segments, hasOneDataAndOneManualSegment, isManualOnly } = useDriverTableSegments({ segmentGroups, data });
  const { sortColumn, sortDirection, handleSort } = useTableSort(segments, hasOneDataAndOneManualSegment);
  const [editedPercentiles, setEditedPercentiles] = React.useState<EditedMetrics>({});

  const selectedDriverType = headroomDriver === "driver1" ? driver1Type : driver2Type;
  const selectedDriverMode = headroomDriver === "driver1" ? driver1Mode : driver2Mode;
  const selectedDriverNumeratorType = headroomDriver === "driver1" ? driver1NumeratorType : driver2NumeratorType;
  const selectedDriverPercentile = headroomDriver === "driver1" ? driver1Percentile : driver2Percentile;

  React.useEffect(() => {
    if (shouldRefetchDriverMetrics) {
      fetchData();
      setShouldRefetchDriverMetrics(false);
      setEditedPercentiles({});
    }
  }, [fetchData, setShouldRefetchDriverMetrics, shouldRefetchDriverMetrics]);

  React.useEffect(() => {
    if (isManualOnly && rowsPerPage !== 100) {
      setRowsPerPage(MAX_ROWS);
      setPage(1);
    }
  }, [isManualOnly, rowsPerPage, setRowsPerPage, setPage]);

  const handlePageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    const maxPage = Math.max(1, Math.ceil(totalCount / rowsPerPage));
    const boundedPage = Math.min(Math.max(1, newPage), maxPage);
    setPage(boundedPage);
    fetchData({ offset: (boundedPage - 1) * rowsPerPage });
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    fetchData({ limit: newRowsPerPage, offset: 0 });
  };

  const handleExport = () => {
    exportDriverMetricsDataToExcel(
      segments,
      selectableHeader1,
      selectableHeader2,
      driver1Type,
      driver2Type,
      driver1Mode,
      driver2Mode,
      driver1NumeratorType,
      driver2NumeratorType,
      driver1Percentile,
      driver2Percentile,
      tableName,
      segments,
      data,
      sortColumn,
      sortDirection,
      editedPercentiles,
      headroomDriver,
      headroomDriverPercentile
    );
  };

  const handleSaveData = async () => {
    if (!data || data.length === 0) {
      return;
    }
    try {
      setIsLoading(true);
      await saveDriverMetricsData(editedPercentiles, data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalculateDriverMetrics = () => {
    fetchData();
  };

  const shouldDisableCalcButton = validateDriversAndSegments({
    tableName,
    segmentGroups,
    count1Column: selectableHeader1.replace(/^Total /, ""),
    driver1Type,
    driver1Mode,
    driver1NumeratorType,
    driver2Type,
    driver2Mode,
    driver2NumeratorType,
  });

  const toolbarButtons = [
    {
      icon: FunctionsIcon,
      tooltip: "Calculate Driver Metrics",
      onClick: handleCalculateDriverMetrics,
      disabled: shouldDisableCalcButton,
      tooltipPlacement: "left-end" as TooltipProps["placement"],
    },
    {
      icon: SaveAltIcon,
      tooltip: "Save data",
      onClick: handleSaveData,
      disabled: !data || driverMetricsResult.loading,
    },
    {
      icon: DownloadIcon,
      tooltip: "Export to Excel",
      onClick: handleExport,
      tooltipPlacement: "top" as TooltipProps["placement"],
      disabled: !data,
    },
  ];

  if (loading) {
    return (
      <StyledTableContainer>
        <TableToolbar buttons={toolbarButtons} showDriverSelect />
        <Table aria-label="skeleton driver metrics table">
          <DriverMetricsTableHeader
            segments={segments}
            driverType={selectedDriverType}
            driverMode={selectedDriverMode}
            driverNumeratorType={selectedDriverNumeratorType}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
            isSkeleton={true}
          />
          <DriverMetricsTableBody
            segments={segments}
            data={data}
            driverMode={selectedDriverMode}
            driverPercentile={selectedDriverPercentile}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
            isSkeleton={true}
            shouldRefetchDriverMetrics={shouldRefetchDriverMetrics}
            editedPercentiles={editedPercentiles}
            setEditedPercentiles={setEditedPercentiles}
          />
        </Table>
      </StyledTableContainer>
    );
  }

  if (error) {
    return (
      <StyledTableContainer>
        <TableToolbar buttons={toolbarButtons} showDriverSelect />
        <MDTypography variant="body1" color="error.main" align="center" p={2}>
          Error: {error.message}
        </MDTypography>
      </StyledTableContainer>
    );
  }

  return (
    <StyledTableContainer>
      <TableToolbar buttons={toolbarButtons} showDriverSelect />
      <Table aria-label="driver metrics table">
        <DriverMetricsTableHeader
          segments={segments}
          driverType={selectedDriverType}
          driverMode={selectedDriverMode}
          driverNumeratorType={selectedDriverNumeratorType}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          handleSort={handleSort}
          hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
          isSkeleton={false}
        />
        <DriverMetricsTableBody
          segments={segments}
          data={data}
          driverMode={selectedDriverMode}
          driverPercentile={selectedDriverPercentile}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          hasOneDataAndOneManualSegment={hasOneDataAndOneManualSegment}
          isSkeleton={false}
          shouldRefetchDriverMetrics={shouldRefetchDriverMetrics}
          editedPercentiles={editedPercentiles}
          setEditedPercentiles={setEditedPercentiles}
        />
      </Table>
      {!isManualOnly && (
        <TablePaginationWrapper
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
    </StyledTableContainer>
  );
};

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(1),
  backgroundColor: colors.background.default,
  borderRadius: theme.spacing(1.5),
  boxShadow: theme.shadows[4],
  padding: theme.spacing(1),
  border: `1px solid ${colors.grey[300]}`,
  overflowX: "auto",
  maxHeight: "800px",
  overflowY: "auto",
}));

export default DriverMetricsTable;
